import { getMarketplaceMerchants } from 'APIs/marketplaces/marketplaces';
import { MarketplaceMerchant, MarketplaceMerchantsQueryParams } from 'APIs/marketplaces/marketplaces.types';
import { QueryParams } from 'APIs/types';
import FilterComponent from 'Components/Common/Filters/FilterComponent';
import { FilterOption } from 'Components/Common/Filters/types';
import { baseUri } from 'Constants/Constants';
import { Content, LpBox } from 'Constants/styles';
import { FunctionComponent, ReactElement, useCallback, useEffect, useState } from 'react';
import { MdFilterList, MdRefresh } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import getErrorMessage from 'utils/getErrorMessage';

import MerchantDataTable from './MerchantDataTable';
import { filterQueryParams, initFilterOptions } from './MerchantFilter';
import { FilterContainer } from './styles';

type Props = {
  merchantId?: string;
};

const MerchantList = ({ merchantId }: Props): ReactElement => {
  const RefreshIcon = MdRefresh as FunctionComponent;
  const FilterListIcon = MdFilterList as FunctionComponent;

  const { marketplaceId } = useParams<{ marketplaceId: string }>();

  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);
  const [merchants, setMerchants] = useState<Array<MarketplaceMerchant>>([]);
  const [filterOptionValues, setFilterOptionValues] = useState<FilterOption[]>(initFilterOptions);
  const [activePage, setActivePage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filterCount, setFilterCount] = useState<number>(initFilterOptions.filter((option) => option.checked).length);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchingError, setFetchingError] = useState<string>('');

  const getFilterQueryParams = useCallback(filterQueryParams, []);

  const fetchMerchants = useCallback(async () => {
    if (!apiBaseUri) {
      return;
    }

    try {
      setIsLoading(true);

      const qParams: QueryParams = getFilterQueryParams(filterOptionValues);

      await getMarketplaceMerchants(apiBaseUri, marketplaceId, {
        ...(qParams as MarketplaceMerchantsQueryParams),
        page: activePage,
        limit: perPage,
        sort: '-createdAt',
      }).then((resp) => {
        setMerchants(resp.items);
        setTotalCount(resp.totalRecords);
      });
    } catch (e) {
      setMerchants([]);
      setTotalCount(0);
      setFetchingError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiBaseUri, filterOptionValues, getFilterQueryParams, marketplaceId, perPage]);

  const handlePerPageChange = useCallback((newPerPage: number, page: number) => {
    setPerPage(newPerPage);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleMerchantManage = (merchantId: string) => {
    window.location.href = `${baseUri}/merchants/${merchantId}/details?marketplaceId=${marketplaceId}`;
  };

  useEffect(() => {
    fetchMerchants();
  }, [activePage, perPage, fetchMerchants]);

  return (
    <Content className="merchant-invoices-content">
      <LpBox>
        <h3 className="mb-0">Merchants</h3>
        <FilterContainer>
          <button className="lp-w-full" onClick={() => fetchMerchants()}>
            Refresh <RefreshIcon />
          </button>
          <button className="lp-w-full" onClick={() => setFilterPopupOpen((prev) => !prev)}>
            Filter {filterCount > 0 && `(${filterCount})`} <FilterListIcon />
          </button>
          {filterPopupOpen && (
            <FilterComponent
              filterOptionValues={filterOptionValues}
              initFilterOptionValues={initFilterOptions}
              setFilterOptionValues={setFilterOptionValues}
              handlePageChange={setActivePage}
              handleFilterCount={setFilterCount}
              toggleFilter={() => setFilterPopupOpen(false)}
            />
          )}
        </FilterContainer>
        <MerchantDataTable
          merchantList={merchants}
          totalRows={totalCount}
          activePage={activePage}
          handlePageChange={handlePageChange}
          handlePerRowsChange={handlePerPageChange}
          handleMerchantManage={handleMerchantManage}
          fetchListLoader={isLoading}
          limit={perPage}
          isErrorRequest={!!fetchingError}
        />
      </LpBox>
    </Content>
  );
};

export default MerchantList;
